import {
  type ConsoleTranscript,
  type ConsoleTranscriptEntry,
} from './transcript';

/**
 * Collects console stdout and stdin events into rendered terminal rows.
 *
 * ## What is a "line"?
 *
 * A line is the text visible in one terminal row. Lines never contain `\n`
 * characters — `\n` is the *delimiter* between lines, not part of any line's
 * content. An empty string (`""`) is a valid line (e.g., from `print()`).
 *
 * ## How stdout is buffered
 *
 * Python's `print()` delivers output in chunks that may or may not end with
 * `\n`, and a single chunk may contain multiple `\n` characters. This class
 * buffers raw chunks and splits on `\n`:
 *
 *   - Completed rows (text before each `\n`) are appended to the entries
 *     immediately as `'print'` entries.
 *   - A trailing partial row (text after the last `\n`, or a chunk with no
 *     `\n` at all) stays in the buffer until the next chunk completes it
 *     or `finish()` flushes it.
 *
 * Example: `appendOutput("Enter name: ")` buffers `"Enter name: "`.
 * A subsequent `appendOutput("!\n")` produces the completed entry
 * `{ type: 'print', output: 'Enter name: !' }`.
 *
 * ## How input is recorded
 *
 * When `recordInput(value)` is called:
 *
 *   1. If there is a pending partial row in the buffer, a new `'input'` entry
 *      is created using the buffered text as the prompt.
 *   2. Otherwise, if we are at a line boundary (no pending output on this
 *      row), a new `'input'` entry with empty prompt is created — this
 *      matches the terminal behavior where `input()` with no prior `print()`
 *      starts on a fresh line.
 *   3. Otherwise, the most recent entry is upgraded from `'print'` to
 *      `'input'`, attaching the recorded value.
 *
 * ## Lifecycle
 *
 *   1. Feed stdout chunks via `appendOutput()` and stdin values via
 *      `recordInput()` in the order they occur during execution.
 *   2. Call `finish()` once at process end to flush any trailing partial
 *      row without adding a spurious blank entry.
 *   3. Read the result via `getEntries()`.
 */
export class ObservedConsoleTranscript {
  private buffer = '';
  private readonly entries: ConsoleTranscriptEntry[] = [];
  private isAtLineBoundary = true;

  /**
   * Splits buffered content on `\n`, returning completed rows and keeping
   * any trailing partial content in the buffer.
   */
  private splitBuffer(chunk: string): string[] {
    const parts = `${this.buffer}${chunk}`.split('\n');
    this.buffer = parts.pop() ?? '';
    return parts;
  }

  /**
   * Returns and clears any trailing partial row from the buffer.
   *
   * Returns `null` when there is no partial row buffered.
   */
  private flushBuffer(): string | null {
    if (this.buffer.length === 0) {
      return null;
    }

    const line = this.buffer;
    this.buffer = '';
    return line;
  }

  /**
   * Records a raw stdout chunk and appends any completed rows.
   */
  appendOutput(chunk: string): void {
    const completedLines = this.splitBuffer(chunk);
    if (completedLines.length === 0) {
      this.isAtLineBoundary = false;
      return;
    }

    for (const line of completedLines) {
      this.entries.push({type: 'print', output: line});
    }
    this.isAtLineBoundary = true;
  }

  /**
   * Records an input value on the current row, creating one when needed.
   */
  recordInput(inputValue: string): void {
    const pendingLine = this.flushBuffer();
    if (pendingLine !== null) {
      this.entries.push({
        type: 'input',
        output: pendingLine,
        input: inputValue,
      });
    } else if (this.entries.length === 0 || this.isAtLineBoundary) {
      this.entries.push({type: 'input', output: '', input: inputValue});
    } else {
      // Buffer is empty and we're mid-row: upgrade the most recent entry
      // from 'print' to 'input', preserving its output as the prompt.
      const lastIndex = this.entries.length - 1;
      this.entries[lastIndex] = {
        type: 'input',
        output: this.entries[lastIndex].output,
        input: inputValue,
      };
    }
    this.isAtLineBoundary = false;
  }

  /**
   * Flushes a trailing partial line at process end without adding blank rows.
   */
  finish(): void {
    const pendingLine = this.flushBuffer();
    if (pendingLine === null) {
      return;
    }

    this.entries.push({type: 'print', output: pendingLine});
    this.isAtLineBoundary = false;
  }

  /**
   * Returns the accumulated transcript entries.
   *
   * Returns a defensive copy so callers cannot mutate the internal state.
   *
   * @return The transcript entries in chronological order.
   */
  getEntries(): ConsoleTranscript {
    return [...this.entries];
  }
}
